import React from 'react';
import type { Column } from '/imports/ui/DataTable';
import { filterBoolean } from '/imports/utils';

interface TableSkeletonProps {
  columns: Column[];
  rows: number;
}

// Widths come from (row, column) rather than from a random draw, so the bars
// keep their shape across re-renders instead of reshuffling on every paint.
// 7 widths stepped by 3 and 2 (both coprime with 7) decorrelates the two axes;
// the values themselves carry no meaning beyond looking like ragged data.
const BAR_WIDTHS = ['w-[80%]', 'w-[55%]', 'w-[68%]', 'w-[42%]', 'w-[88%]', 'w-[60%]', 'w-[35%]'];
const barWidth = (row: number, column: number) => BAR_WIDTHS[(row * 3 + column * 2) % BAR_WIDTHS.length];

/**
 * Placeholder rows that mirror the real column layout while a table loads.
 *
 * The bars are `inline-block`, not `block`, on purpose. Cells are `py-2.5`
 * around a 13px line box, and a block-level 12px bar would drop the line box
 * entirely and collapse rows to 32px, resizing the table when data arrived.
 * Inline-block keeps the line box, landing rows at 36px.
 *
 * That is close to, but not always exactly, the real row height: a row is as
 * tall as its tallest cell, and a `.mono` column (JetBrains Mono, 18px line box
 * vs Inter's 15px) pushes it to 39px. Matching that is not worth chasing --
 * mono is applied inside a column's `render`, so it is not visible from the
 * Column metadata, and 3px is imperceptible next to getting the row *count*
 * right, which is what actually keeps the scroll height stable.
 */
export const TableSkeleton = (props: TableSkeletonProps) => {
  const columns = filterBoolean(props.columns);
  return (
    <>
      {Array.from({ length: props.rows }, (_, r) => (
        // Decorative: the tbody carries aria-busy and the caption carries the
        // status text, so these rows are noise to a screen reader.
        <tr key={`skeleton-${r}`} className="skeleton-row" aria-hidden="true">
          <td className="row-select"><span className="skeleton-bar inline-block h-3.5 w-3.5 align-middle" /></td>
          {columns.map((c, i) => (
            <td key={String(c.field)}>
              <span className={`skeleton-bar inline-block h-3 align-middle ${barWidth(r, i)}`} />
            </td>
          ))}
          <td className="row-actions"><span className="skeleton-bar inline-block h-3 w-8 align-middle" /></td>
        </tr>
      ))}
    </>
  );
};
